/**
 * The marks a customer needs to see to lay out a card correctly. §6.3.
 *
 * Three boxes, and the whole point is that a customer can tell them apart at a
 * glance, because confusing them is what causes reprints:
 *
 *   Bleed   outside the cut. Artwork must run to here, and everything here
 *           gets thrown away. Red, because it is the one that costs money.
 *   Trim    where the guillotine lands. Solid and dark: it is the edge of the
 *           finished card, and the only line that is a real object.
 *   Safe    inside the cut. Anything that must survive stays in here. Blue,
 *           the colour every print template uses for it.
 *
 * The colours follow what printers put on their own templates rather than
 * anything this editor invented - a customer who has downloaded a template
 * before already knows what a red outer line and a blue inner one mean.
 *
 * They are labelled as well as coloured. A line is only self-explanatory to
 * someone who already knows, and the labels drop away when the zoom leaves no
 * room for them rather than piling up on top of each other.
 *
 * All of it is drawn without listening for events, so clicking near a line
 * selects whatever is under it rather than the line.
 */

import { Circle, Group, Line, Path, Rect, Text } from 'react-konva';
import { mmToPx } from '../model/units';

interface Props {
  /** Trim size, in mm - the finished card. */
  width: number;
  height: number;
  /**
   * Rounded corners, in mm. The cut and the safe area follow them; the bleed
   * stays square, because artwork still has to run past the corner the cutter
   * takes away.
   */
  radius?: number;
  /**
   * A shaped cut (a tapered mug's fan), as SVG path data in mm. Drawn as the
   * trim, with the page outside it shaded: it is thrown away.
   */
  cut?: string;
  /** That cut is a circle, so the safe area is a smaller concentric one. */
  round?: boolean;
  /** Where the sheet folds, in mm from the trim's top-left. */
  folds?: { across: number[]; down: number[] };
  /** What each panel between the folds is called, left to right. */
  panels?: string[];
  /**
   * Two printed pages side by side: the gutter down the middle, and the margin
   * either side of it that a binding eats.
   */
  spread?: boolean;
  bleed: number;
  safe: number;
  zoom: number;
  /** Preview shows the card as trimmed, so none of this belongs on it. */
  preview: boolean;
  /**
   * Whether to name the lines. The page being edited says Bleed, Trim and
   * Safe; the others show the same lines without the words, which would only
   * repeat themselves down the screen.
   */
  labels?: boolean;
  /** Parts of the sheet the hardware hides. */
  shade?: Array<{ x: number; y: number; w: number; h: number; name?: string }>;
  /** Where the eyelets are punched, in page millimetres. */
  eyelets?: Array<{ x: number; y: number }>;
}

/** Where the guillotine lands. The edge of the real object. */
const TRIM = '#16181c';

/** Outside the cut. Red, as on every printer's template. */
const BLEED = '#d93a3a';

/** Inside the cut. Blue, likewise. */
const SAFE = '#2f6fed';

/**
 * Where the sheet folds. Violet, which is neither of the other two and is what
 * most printers' templates use for a fold.
 */
const FOLD = '#7b4fd1';

/**
 * What the hardware swallows. Grey rather than a warning colour: it is not a
 * mistake to print there - the sheet has to be printed to the edge - it is only
 * a mistake to put something there that anybody needs to see.
 */
const HIDDEN = '#16181c';

/** Below this there is no room for a label, so it is left off. */
/**
 * The holes. Their own colour because they are the one mark on the page that
 * is a hole rather than a line: everything else can be designed over.
 */
const EYELET = '#0f9d58';

/** Ring radius in millimetres, drawn at the size a 12 mm eyelet really is. */
const EYELET_MM = 6;

const LABEL_ROOM = 15;

const LABEL_SIZE = 10;

export function PrintMarks({ width, height, radius: radiusMm = 0, cut, round, folds, panels, spread, bleed, safe, zoom, preview, labels = true, shade, eyelets }: Props) {
  const w = mmToPx(width, zoom);
  const h = mmToPx(height, zoom);
  const b = mmToPx(bleed, zoom);
  const s = mmToPx(safe, zoom);
  const r = mmToPx(radiusMm, zoom);

  if (preview) return null;

  // Hairlines, held at one screen pixel whatever the zoom. A trim line that
  // thickens as you zoom in stops being a trim line and becomes a band you
  // cannot align to.
  const hair = 1;

  return (
    <Group listening={false}>
      {/* What the hardware hides: drawn first, so everything else reads over it. */}
      {(shade ?? []).map((band, index) => (
        <Group key={`shade-${index}`} listening={false}>
          <Rect
            x={mmToPx(band.x, zoom)}
            y={mmToPx(band.y, zoom)}
            width={mmToPx(band.w, zoom)}
            height={mmToPx(band.h, zoom)}
            fill={HIDDEN}
            opacity={0.1}
          />
          {labels && band.name && mmToPx(band.h, zoom) >= LABEL_ROOM && (
            <Mark text={band.name} x={mmToPx(band.x, zoom) + 3} y={mmToPx(band.y, zoom) + 2} fill={HIDDEN} />
          )}
        </Group>
      ))}

      {/* Bleed: where artwork must run to, outside the cut. */}
      <Rect
        x={-b}
        y={-b}
        width={w + b * 2}
        height={h + b * 2}
        stroke={BLEED}
        strokeWidth={hair}
        dash={[5, 3]}
        strokeScaleEnabled={false}
      />

      {/* Trim: where the guillotine lands - or the cutter, round a shaped page. */}
      {cut ? (
        <>
          <Path
            data={`M ${-bleed} ${-bleed} H ${width + bleed} V ${height + bleed} H ${-bleed} Z ${cut}`}
            scaleX={w / width}
            scaleY={h / height}
            fill="rgba(22, 24, 28, 0.12)"
            fillRule="evenodd"
          />
          <Path id="tb-trim" data={cut} scaleX={w / width} scaleY={h / height} stroke={TRIM} strokeWidth={hair} strokeScaleEnabled={false} />
        </>
      ) : (
        <Rect id="tb-trim" x={0} y={0} width={w} height={h} cornerRadius={r} stroke={TRIM} strokeWidth={hair} strokeScaleEnabled={false} />
      )}

      {/*
        Safe: keep anything that must survive inside this.

        A shaped cut has no straight safe box - except a circle, whose inside
        is simply a smaller circle. A Rect whose corner radius is half its
        short side *is* that circle, so the round page and the square one are
        the same mark drawn twice, rather than two marks that can disagree.
      */}
      {( ! cut || round ) && <Rect
        x={s}
        y={s}
        width={Math.max(0, w - s * 2)}
        height={Math.max(0, h - s * 2)}
        // The inside of a rounded cut, the same distance in all the way round.
        cornerRadius={round ? Math.max(0, (Math.min(w, h) - s * 2) / 2) : Math.max(0, r - s)}
        stroke={SAFE}
        strokeWidth={hair}
        dash={[3, 3]}
        strokeScaleEnabled={false}
      />}

      <CornerMarks w={w} h={h} b={b} />

      {/*
        The gutter of a spread: where the two pages meet and the binding takes
        hold. Anything across it is bent, and on a thick catalogue some of it
        disappears into the spine, so the margin either side is marked as
        firmly as the trim is.
      */}
      {spread && (
        <Folds
          folds={{ across: [width / 2], down: [] }}
          panels={[]}
          w={w}
          h={h}
          b={b}
          safe={s}
          zoom={zoom}
          width={width}
          labels={false}
        />
      )}

      {/*
        The folds, and the margin either side of each: anything that crosses a
        fold is creased, so type and faces have to stay clear of it. Drawn over
        the safe box because a fold is the tighter rule where the two meet.
      */}
      <Folds
        folds={folds}
        panels={panels}
        w={w}
        h={h}
        b={b}
        safe={s}
        zoom={zoom}
        width={width}
        labels={labels}
      />

      {/*
        Labels sit at the top-left of their own line and read outward, so they
        never land on the middle of the card where the artwork is.
      */}
      {labels && b >= LABEL_ROOM && <Mark text="Bleed" x={-b + 3} y={-b + 2} fill={BLEED} />}
      {labels && b >= LABEL_ROOM && <Mark text="Trim" x={3} y={-LABEL_SIZE - 3} fill={TRIM} />}
      {labels && !cut && s >= LABEL_ROOM && <Mark text="Safe" x={s + 3} y={s + 2} fill={SAFE} />}

      {/*
        The eyelets, drawn last and over everything.

        Every other mark on this page describes where the artwork may go. An
        eyelet is a hole punched through whatever is under it, so it is the one
        mark that has to stay visible when the artwork is on top of it - which
        is exactly the case the customer needs to see.
      */}
      {(eyelets ?? []).map((hole, index) => (
        <Group key={`eyelet-${index}`} listening={false}>
          <Circle
            x={mmToPx(hole.x, zoom)}
            y={mmToPx(hole.y, zoom)}
            radius={mmToPx(EYELET_MM, zoom)}
            stroke={EYELET}
            strokeWidth={hair}
            fill="#ffffff"
            opacity={0.85}
          />
          <Circle
            x={mmToPx(hole.x, zoom)}
            y={mmToPx(hole.y, zoom)}
            radius={mmToPx(EYELET_MM, zoom) * 0.45}
            stroke={EYELET}
            strokeWidth={hair}
          />
        </Group>
      ))}

      {labels && (eyelets?.length ?? 0) > 0 && (
        <Mark text={`${eyelets!.length} eyelets`} x={3} y={h + 4} fill={EYELET} />
      )}
    </Group>
  );
}

/** One label. Held at a fixed size, so it stays readable at any zoom. */
function Mark({ text, x, y, fill }: { text: string; x: number; y: number; fill: string }) {
  return (
    <Text
      x={x}
      y={y}
      text={text}
      fontSize={LABEL_SIZE}
      fontStyle="600"
      fontFamily="system-ui, sans-serif"
      fill={fill}
      listening={false}
    />
  );
}

/**
 * Crop marks at the four corners, offset into the bleed.
 *
 * They are what a printer looks for first, and their presence tells a customer
 * this is a real print file rather than a picture of one.
 */
function CornerMarks({ w, h, b }: { w: number; h: number; b: number }) {
  const length = Math.min(b, 12);

  if (length < 3) return null;

  const gap = 2;

  const marks: Array<[number, number, number, number]> = [
    // Top left.
    [-b, 0, -gap, 0],
    [0, -b, 0, -gap],
    // Top right.
    [w + gap, 0, w + b, 0],
    [w, -b, w, -gap],
    // Bottom left.
    [-b, h, -gap, h],
    [0, h + gap, 0, h + b],
    // Bottom right.
    [w + gap, h, w + b, h],
    [w, h + gap, w, h + b],
  ];

  return (
    <>
      {marks.map((points, i) => (
        <Line key={i} points={points} stroke={TRIM} strokeWidth={1} strokeScaleEnabled={false} />
      ))}
    </>
  );
}

/**
 * Fold lines, their margins, and what each panel is called.
 *
 * The line itself is where the crease lands; the pair of faint lines either
 * side of it is the same distance a customer must keep clear of the trim,
 * because a crease damages ink as surely as a guillotine does.
 */
function Folds({
  folds,
  panels,
  w,
  h,
  b,
  safe,
  zoom,
  width,
  labels,
}: {
  folds?: { across: number[]; down: number[] };
  panels?: string[];
  w: number;
  h: number;
  b: number;
  safe: number;
  zoom: number;
  /** The page's width in millimetres, for placing the panel names. */
  width: number;
  labels: boolean;
}) {
  if (!folds || (folds.across.length === 0 && folds.down.length === 0)) return null;

  const hair = 1;
  const lines: React.ReactNode[] = [];

  const draw = (key: string, points: number[], faint: boolean) => (
    <Line
      key={key}
      points={points}
      stroke={FOLD}
      strokeWidth={hair}
      opacity={faint ? 0.45 : 1}
      dash={faint ? [2, 3] : [7, 4]}
      strokeScaleEnabled={false}
      listening={false}
    />
  );

  folds.across.forEach((at, i) => {
    const x = mmToPx(at, zoom);

    lines.push(draw(`a${i}`, [x, -b, x, h + b], false));

    if (safe > 0) {
      lines.push(draw(`a${i}-l`, [x - safe, 0, x - safe, h], true));
      lines.push(draw(`a${i}-r`, [x + safe, 0, x + safe, h], true));
    }
  });

  folds.down.forEach((at, i) => {
    const y = mmToPx(at, zoom);

    lines.push(draw(`d${i}`, [-b, y, w + b, y], false));

    if (safe > 0) {
      lines.push(draw(`d${i}-t`, [0, y - safe, w, y - safe], true));
      lines.push(draw(`d${i}-b`, [0, y + safe, w, y + safe], true));
    }
  });

  /*
   * Panel names, centred in each panel along the top. Only for folds across,
   * which is what a brochure is; a sheet folded both ways would need a grid of
   * names and nobody prints one.
   */
  const names: React.ReactNode[] = [];

  if (labels && folds.across.length > 0 && w > 0) {
    const edges = [0, ...folds.across, width];

    for (let i = 0; i < edges.length - 1; i++) {
      const from = edges[i]!;
      const to = edges[i + 1]!;
      const middle = mmToPx((from + to) / 2, zoom);
      const room = mmToPx(to - from, zoom);
      const text = panels?.[i] || `Panel ${i + 1}`;

      // No room for a name is better than names on top of each other.
      if (room < 48) continue;

      names.push(
        <Text
          key={`n${i}`}
          x={middle - room / 2}
          y={-b - LABEL_SIZE - 2}
          width={room}
          align="center"
          text={text}
          fontSize={LABEL_SIZE}
          fontStyle="600"
          fontFamily="system-ui, sans-serif"
          fill={FOLD}
          listening={false}
        />
      );
    }
  }

  return (
    <>
      {lines}
      {names}
    </>
  );
}
